import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  History as HistoryIcon,
  Restore as RestoreIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import log from 'loglevel';
import { FilterValue } from '@/API';
import { generateClient } from 'aws-amplify/api';
import { searchFilterValues } from '@/graphql/queries';
import { houseListFilters } from '@/pages/HouseListFilterPage';

interface MLInactiveFlagsVersionHistoryProps {
  onRestoreVersion: (version: FilterValue) => void;
  disabled?: boolean;
}

const MLInactiveFlagsVersionHistory: React.FC<
  MLInactiveFlagsVersionHistoryProps
> = ({ onRestoreVersion, disabled = false }) => {
  const [versions, setVersions] = useState<FilterValue[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<FilterValue | null>(
    null
  );

  // Find the ML Active filter definition to get the correct fieldId
  const mlActiveFilter = useMemo(() => {
    const filter = houseListFilters.find(
      filter => filter.label === 'ML Active'
    );
    if (!filter) {
      log.error('MLInactiveFlagsVersionHistory - ML Active filter not found');
    }
    return filter;
  }, []);

  // Load actual version history from API
  const loadVersionHistory = useCallback(async () => {
    if (!mlActiveFilter) {
      log.error('Cannot load version history - ML Active filter not found');
      return;
    }

    setLoading(true);
    try {
      const client = generateClient();
      const response = await client.graphql({
        query: searchFilterValues,
        variables: {
          filter: {
            and: [
              { listType: { eq: 'houselist' } },
              { fieldId: { eq: mlActiveFilter.id } },
            ],
          },
          limit: 10, // Limit to last 10 changes
        },
      });

      if ('data' in response && response.data?.searchFilterValues?.items) {
        const items = response.data.searchFilterValues.items.filter(
          (item: FilterValue | null): item is FilterValue => item !== null
        );
        // Sort by version in descending order - since version is now a string, parse it as number for sorting
        const sortedItems = items.sort(
          (a, b) => parseInt(b.version || '0') - parseInt(a.version || '0')
        );
        setVersions(sortedItems);
      }
    } catch (error) {
      log.error('Error loading version history:', error);
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }, [mlActiveFilter]);

  // Load version history on mount and when refreshTrigger changes
  useEffect(() => {
    if (mlActiveFilter) {
      void loadVersionHistory();
    }
  }, [loadVersionHistory, mlActiveFilter]);

  // Also refresh when expanded (for immediate update when user opens)
  useEffect(() => {
    if (expanded && mlActiveFilter) {
      void loadVersionHistory();
    }
  }, [expanded, loadVersionHistory, mlActiveFilter]);

  const handleRestoreClick = (version: FilterValue) => {
    setSelectedVersion(version);
    setRestoreDialogOpen(true);
  };

  const handleConfirmRestore = () => {
    if (selectedVersion) {
      onRestoreVersion(selectedVersion);
      setRestoreDialogOpen(false);
      setSelectedVersion(null);
    }
  };

  const getChangeDescription = (
    current: FilterValue,
    previous?: FilterValue
  ) => {
    if (!previous) return 'Initial configuration';

    const added = current.selectedValues.filter(
      flag => !previous.selectedValues.includes(flag)
    );
    const removed = previous.selectedValues.filter(
      flag => !current.selectedValues.includes(flag)
    );

    const changes = [];
    if (added.length > 0) {
      changes.push(`Added: ${added.map(f => f.replace(/_/g, ' ')).join(', ')}`);
    }
    if (removed.length > 0) {
      changes.push(
        `Removed: ${removed.map(f => f.replace(/_/g, ' ')).join(', ')}`
      );
    }

    return changes.length > 0 ? changes.join('; ') : 'No changes';
  };

  return (
    <Card>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <HistoryIcon />
            <Typography variant="h6">Version History</Typography>
          </Box>
        }
        action={
          <IconButton
            onClick={() => setExpanded(!expanded)}
            disabled={disabled}
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s',
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        }
      />

      <Collapse in={expanded}>
        <CardContent>
          {loading ? (
            <Typography>Loading version history...</Typography>
          ) : versions.length === 0 ? (
            <Alert severity="info">No version history available yet.</Alert>
          ) : (
            <List>
              {versions.map((version, index) => {
                const previousVersion = versions[index + 1];
                const isLatest = index === 0;

                return (
                  <ListItem
                    key={version.id}
                    sx={{
                      border: '1px solid',
                      borderColor: isLatest ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      mb: 1,
                      bgcolor: isLatest ? 'primary.50' : 'background.paper',
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography variant="subtitle1">
                            Version {version.version}
                            {isLatest && (
                              <Chip
                                label="Current"
                                size="small"
                                color="primary"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Typography>
                          <Box display="flex" gap={1}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatDistanceToNow(
                                new Date(version.createdAt),
                                { addSuffix: true }
                              )}
                            </Typography>
                            {!isLatest && (
                              <Button
                                size="small"
                                startIcon={<RestoreIcon />}
                                onClick={() => handleRestoreClick(version)}
                                disabled={disabled}
                              >
                                Restore
                              </Button>
                            )}
                          </Box>
                        </Box>
                      }
                      secondary={
                        <Box mt={1}>
                          <Typography variant="body2" color="text.secondary">
                            Changed by:{' '}
                            {version.changedBy.replace('@erickson.com', '')}
                          </Typography>
                          {version.description && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 0.5 }}
                            >
                              Description: {version.description}
                            </Typography>
                          )}
                          {version.changeReason && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 0.5 }}
                            >
                              Change reason: {version.changeReason}
                            </Typography>
                          )}
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            Changes:{' '}
                            {getChangeDescription(version, previousVersion)}
                          </Typography>
                          <Box display="flex" gap={2} mt={1}>
                            <Typography variant="caption">
                              Flags: {version.selectedValues.length}
                            </Typography>
                          </Box>
                          <Box mt={1} display="flex" flexWrap="wrap" gap={0.5}>
                            {version.selectedValues.map(flag => (
                              <Chip
                                key={flag}
                                label={flag.replace(/_/g, ' ')}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            ))}
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </CardContent>
      </Collapse>

      {/* Restore Confirmation Dialog */}
      <Dialog
        open={restoreDialogOpen}
        onClose={() => setRestoreDialogOpen(false)}
      >
        <DialogTitle>Restore Version {selectedVersion?.version}?</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            This will restore the ML Inactive Flags configuration to version{' '}
            {selectedVersion?.version}.
          </Typography>
          {selectedVersion && (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                This version includes the following flags:
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5} mt={1}>
                {selectedVersion.selectedValues.map(flag => (
                  <Chip
                    key={flag}
                    label={flag.replace(/_/g, ' ')}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
              {selectedVersion.description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  Description: {selectedVersion.description}
                </Typography>
              )}
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action will create a new version in the history and cannot be
            undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmRestore}
            variant="contained"
            color="primary"
          >
            Restore Version
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default MLInactiveFlagsVersionHistory;
